/**
 * Safe Math Expression Evaluator
 * 
 * Evaluates mathematical expressions in a sandboxed environment.
 * Used for expression-based validation.
 */
import { create, all, MathJsStatic, FactoryFunctionMap } from 'mathjs';

// Create a sandboxed mathjs instance
const math = create(all as FactoryFunctionMap) as MathJsStatic;

// List of potentially dangerous functions to disable
const BLOCKED_FUNCTIONS = [
  'import',
  'createUnit',
  // Note: We keep 'parse', 'compile', 'evaluate', and 'typed' since they're needed for basic operations
  'chain',
  'resolve',
  'simplify',
  'derivative',
  'rationalize',
  'help'
];

// Remove dangerous functions from the math instance
for (const fn of BLOCKED_FUNCTIONS) {
  try {
    // @ts-ignore - dynamically removing functions
    if ((math as any)[fn]) {
      delete (math as any)[fn];
    }
  } catch {
    // Some functions may not be deletable, that's okay
  }
}

/**
 * Check for invalid operator patterns that mathjs might parse but shouldn't be allowed
 */
function hasInvalidOperatorPattern(expression: string): boolean {
  // Check for consecutive operators like +++, ---, ***, etc.
  // Allow ++ and -- as they could be valid in some contexts, but not +++
  const invalidPatterns = [
    /\+{3,}/,  // 3 or more consecutive +
    /-{3,}/,   // 3 or more consecutive -
    /\*{2,}/,  // 2 or more consecutive *
    /\/{2,}/,  // 2 or more consecutive /
    /\^{2,}/,  // 2 or more consecutive ^
    /\({3,}/,  // 3 or more consecutive (
    /\){3,}/,  // 3 or more consecutive )
  ];
  
  return invalidPatterns.some(pattern => pattern.test(expression));
}

/**
 * Evaluate a mathematical expression with the given variable scope
 * 
 * @param expression - The expression to evaluate (e.g., "(a * b) + (c * d) == 1200")
 * @param scope - Variable bindings (e.g., { a: 10, b: 5, c: 20, d: 30 })
 * @returns The boolean result of the expression
 * @throws Error if evaluation fails
 */
export function evaluate(
  expression: string,
  scope: Record<string, number>
): boolean {
  try {
    // Validate input
    if (typeof expression !== 'string' || expression.trim().length === 0) {
      throw new Error('Expression must be a non-empty string');
    }

    // Normalize JavaScript operators to MathJS operators
    // MathJS uses == and != instead of === and !==
    expression = expression.replace(/===/g, '==').replace(/!==/g, '!=');

    // Check for invalid operator patterns
    if (hasInvalidOperatorPattern(expression)) {
      throw new Error('Expression contains invalid operator patterns');
    }

    // Validate scope values
    for (const [key, value] of Object.entries(scope)) {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`Invalid value for variable '${key}': must be a finite number`);
      }
    }

    // Freeze scope to prevent modification
    const frozenScope = Object.freeze({ ...scope });

    // Parse the expression
    const node = math.parse(expression);

    // Compile to executable code
    const code = node.compile();

    // Evaluate with scope
    const result = code.evaluate(frozenScope);

    // Ensure we return a boolean
    if (typeof result === 'boolean') {
      return result;
    }

    // mathjs might return a number for comparisons in some cases
    if (typeof result === 'number') {
      return result !== 0;
    }

    // Handle mathjs boolean-like objects
    if (result && typeof result.valueOf === 'function') {
      const value = result.valueOf();
      if (typeof value === 'boolean') {
        return value;
      }
    }

    // Default to false for non-boolean results
    console.warn('Expression did not return a boolean:', expression, '→', result);
    return false;

  } catch (error) {
    // Re-throw with more context
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to evaluate expression "${expression}": ${message}`);
  }
}

/**
 * Check if an expression is syntactically valid
 * 
 * @param expression - The expression to check
 * @returns true if valid, false otherwise
 */
export function isValidExpression(expression: string): boolean {
  try {
    // Normalize JavaScript operators to MathJS operators
    expression = expression.replace(/===/g, '==').replace(/!==/g, '!=');
    
    // Check for invalid operator patterns first
    if (hasInvalidOperatorPattern(expression)) {
      return false;
    }
    
    math.parse(expression);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extract variable names from an expression
 *
 * @param expression - The expression to analyze
 * @param validVariables - Optional list of valid variable names to filter by (e.g., from exercise widgets)
 * @returns Array of variable names
 */
export function extractVariables(expression: string, validVariables?: string[]): string[] {
  try {
    // Normalize JavaScript operators to MathJS operators
    expression = expression.replace(/===/g, '==').replace(/!==/g, '!=');

    const node = math.parse(expression);
    const variables = new Set<string>();

    // Walk the expression tree to find symbol nodes
    node.traverse((node: any) => {
      if (node.isSymbolNode && !isMathConstant(node.name)) {
        variables.add(node.name);
      }
    });

    const extractedVars = Array.from(variables).sort();

    // If validVariables is provided, only return variables that are in the valid list
    if (validVariables && validVariables.length > 0) {
      return extractedVars.filter(v => validVariables.includes(v));
    }

    return extractedVars;
  } catch {
    return [];
  }
}

/**
 * Check if a name is a math constant (not a variable)
 */
function isMathConstant(name: string): boolean {
  const constants = ['pi', 'e', 'i', 'Infinity', 'NaN', 'null', 'undefined', 'true', 'false'];
  return constants.includes(name);
}

/**
 * Validate that an expression will return a boolean
 * 
 * @param expression - The expression to check
 * @returns Error message if invalid, null if valid
 */
export function validateBooleanExpression(expression: string): string | null {
  try {
    // Normalize JavaScript operators to MathJS operators
    expression = expression.replace(/===/g, '==').replace(/!==/g, '!=');
    
    const node = math.parse(expression);
    
    // Check if the root node is a comparison or logical operation
    const validRootOps = ['==', '!=', '<', '>', '<=', '>=', 'and', 'or', 'not', 'xor'];
    
    // Type guard for operator nodes
    if ((node as any).isOperatorNode && validRootOps.includes((node as any).op)) {
      return null;
    }

    // Type guard for constant nodes
    if ((node as any).isConstantNode && typeof (node as any).value === 'boolean') {
      return null;
    }

    return 'Expression should be a comparison (e.g., "a + b == 100")';

  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid expression syntax';
  }
}
